import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import { Vector as VectorLayer } from 'ol/layer.js';
import { Vector as VectorSource } from 'ol/source.js';
import { ExploitationContext } from 'utils/context';
import {
  createNewMap,
  gpsToPixel,
  handleMouseMoveEvent,
  setPolygonStyle,
  zoomToFit,
} from './MapCommon';

const POPUP_OFFSET_X = -50;
const POPUP_GAP_Y = 10;

const MapInteractionHook = ({
  mapId,
  parcelsData,
  initialYear,
  minYear,
  maxYear,
  getCultureForParcel,
  getPolygonStyleParams,
  onParcelClick,
  dependencies = [],
}) => {
  const { culturesColors } = useContext(ExploitationContext);
  const [year, setYear] = useState(initialYear);
  const [parcelInfo, setParcelInfo] = useState(null);
  const [modalCoords, setModalCoords] = useState(null);
  const [cultureHoverName, setCultureHoverName] = useState(null);
  const [parcelHoverCultureName, setParcelHoverCultureName] = useState(null);
  const [map, setMap] = useState(null);
  const [polygonSource, setPolygonSource] = useState(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [popupHeight, setPopupHeight] = useState(0);

  const prevParcelId = useRef(null);
  const hoveredParcelIdRef = useRef(null);
  const storeParcelColorRef = useRef([]);
  const mapElement = useRef();
  const popup = useRef(null);
  const popupContent = useRef(null);
  const eventListenersRef = useRef({ pointermove: null, click: null });
  const callbackRefs = useRef({ getCultureForParcel, getPolygonStyleParams, onParcelClick });
  const hasInitialZoom = useRef(false);

  useEffect(() => {
    callbackRefs.current = { getCultureForParcel, getPolygonStyleParams, onParcelClick };
  });

  const getFeatureId = useCallback((parcel) => `${parcel.id}${year}${parcel.name}`, [year]);

  const findParcelByFeature = useCallback(
    (feature) =>
      parcelsData?.find((parcel) => `${parcel.id}${year}${parcel.name}` === feature.getId()),
    [parcelsData, year],
  );

  const isCultureMatch = (culture, targetName) => {
    if (Array.isArray(culture)) {
      return culture.some((c) => c.name === targetName);
    }
    return culture?.name === targetName;
  };

  const getStoredColor = (featureId) => storeParcelColorRef.current.find((p) => p.id === featureId);

  const applyFeatureStyle = useCallback(
    (feature, parcel, opacity) => {
      const { getCultureForParcel: getCulture, getPolygonStyleParams: getStyle } =
        callbackRefs.current;
      const culture = getCulture(parcel, year);
      const styleParams = getStyle(parcel, culture, opacity);
      setPolygonStyle({ parcelFeature: feature, culturesColors, ...styleParams });
      return culture;
    },
    [year, culturesColors],
  );

  const shouldDimFeature = (selectedFeature, feature) => {
    if (selectedFeature.get('isGroupedParcel')) {
      return (
        !selectedFeature.get('groupChildrenParcels')?.includes(feature.get('parcelId')) &&
        feature.getId() !== selectedFeature.getId() &&
        feature.get('parcelId') !== selectedFeature.get('parent')
      );
    }
    return feature.getId() !== selectedFeature.getId();
  };

  if (parcelInfo?.id !== prevParcelId.current) {
    const isFirstAppearance = prevParcelId.current === null;
    prevParcelId.current = parcelInfo?.id;
    if (isFirstAppearance && popupHeight !== 0) {
      setPopupHeight(0);
    }
  }

  useEffect(() => {
    return handleMouseMoveEvent(setCoords);
  }, []);

  useEffect(() => {
    setMap(createNewMap(mapId));
  }, []);

  useEffect(() => {
    if (map) {
      buildMapLayers();
      if (!hasInitialZoom.current) {
        map.once('rendercomplete', () => {
          zoomToFit(map);
          hasInitialZoom.current = true;
        });
      }
    }
  }, [map, parcelsData, ...dependencies]);

  useEffect(() => {
    if (map) {
      buildMapLayers();
    }
  }, [year, parcelsData]);

  useEffect(() => {
    if (!map || !cultureHoverName || !polygonSource) return;

    const extent = map.getView().calculateExtent(map.getSize());
    polygonSource.forEachFeatureInExtent(extent, (feature) => {
      const parcel = findParcelByFeature(feature);
      if (!parcel) return;

      const culture = callbackRefs.current.getCultureForParcel(parcel, year);
      const isMatch = isCultureMatch(culture, cultureHoverName);
      applyFeatureStyle(feature, parcel, isMatch || cultureHoverName === 'null' ? 1 : 0.3);
    });
  }, [cultureHoverName]);

  const buildMapLayers = () => {
    if (!map || !year) return;

    if (eventListenersRef.current.pointermove) {
      map.un('pointermove', eventListenersRef.current.pointermove);
    }
    if (eventListenersRef.current.click) {
      map.un('click', eventListenersRef.current.click);
    }

    map
      .getLayers()
      .getArray()
      .filter((layer) => layer.get('id') === 'polygonVector')
      .forEach((layer) => map.removeLayer(layer));

    const source = new VectorSource();
    const colorStore = [];

    parcelsData
      ?.filter((parcel) => parcel.coordinates)
      .forEach((parcel) => {
        const featureId = getFeatureId(parcel);

        const feature = new Feature({
          geometry: new Polygon(gpsToPixel(parcel.coordinates)),
        });
        feature.setId(featureId);
        feature.set('parcelId', parcel.id);
        feature.set('parcelYear', year);

        if (parcel.isGroupedParcel) {
          feature.set('isGroupedParcel', true);
          feature.set('groupChildrenParcels', parcel.groupChildrenParcels);
          feature.set('groupSurface', parcel.groupSurface);
        }
        if (parcel.parent) {
          feature.set('parent', parcel.parent);
        }

        const culture = applyFeatureStyle(feature, parcel, 1);

        colorStore.push({
          id: featureId,
          culture: Array.isArray(culture) ? culture : culture?.name,
        });

        source.addFeature(feature);
      });

    storeParcelColorRef.current = colorStore;
    setPolygonSource(source);

    const polygonVector = new VectorLayer({ id: 'polygonVector', source });
    map.addLayer(polygonVector);

    if (hoveredParcelIdRef.current) {
      const hoveredId = hoveredParcelIdRef.current;
      const extent = map.getView().calculateExtent(map.getSize());
      source.forEachFeatureInExtent(extent, (feature) => {
        const parcel = findParcelByFeature(feature);
        if (!parcel) return;
        applyFeatureStyle(feature, parcel, feature.get('parcelId') === hoveredId ? 1 : 0.3);
      });
    }

    let selected = null;

    const onPointerMove = (e) => {
      const extent = map.getView().calculateExtent(map.getSize());

      if (selected !== null) {
        setParcelInfo(null);
        setModalCoords(null);
        hoveredParcelIdRef.current = null;

        source.forEachFeatureInExtent(extent, (feature) => {
          const stored = getStoredColor(feature.getId());
          const parcel = findParcelByFeature(feature);
          if (stored?.culture && parcel) {
            applyFeatureStyle(feature, parcel, 1);
          }
          setParcelHoverCultureName(null);
        });
        selected = null;
      }

      map.forEachFeatureAtPixel(e.pixel, (f) => {
        selected = f;
        const parcel = findParcelByFeature(f);
        if (parcel) {
          setParcelInfo(parcel);
          hoveredParcelIdRef.current = parcel.id;
        }
        setModalCoords(e.pixel);

        source.forEachFeatureInExtent(extent, (feature) => {
          const stored = getStoredColor(feature.getId());
          const featureParcel = findParcelByFeature(feature);
          if (!stored?.culture || !featureParcel) return;

          if (shouldDimFeature(selected, feature)) {
            applyFeatureStyle(feature, featureParcel, 0.3);
          } else {
            setParcelHoverCultureName(stored.culture);
          }
        });
        return true;
      });
    };

    const onClick = (e) => {
      map.forEachFeatureAtPixel(e.pixel, (feature) => {
        const { onParcelClick: clickHandler } = callbackRefs.current;
        if (clickHandler) {
          const parcelId = feature.get('parent') || feature.get('parcelId');
          const parcel = parcelsData?.find((p) => p.id === parcelId);
          const clickedParcel = parcelsData?.find((p) => p.id === feature.get('parcelId'));
          clickHandler(parcel, feature.get('parcelYear'), minYear, clickedParcel);
        }
      });
    };

    map.on('pointermove', onPointerMove);
    map.on('click', onClick);

    eventListenersRef.current = { pointermove: onPointerMove, click: onClick };
  };

  const handleCultureHover = useCallback((name) => {
    setCultureHoverName(name);
  }, []);

  const clearPopup = useCallback(() => {
    setParcelInfo(null);
    setModalCoords(null);
    prevParcelId.current = null;
    setPopupHeight(0);
  }, []);

  const popupContentRef = useCallback(
    (el) => {
      popupContent.current = el;
      if (el) {
        const h = el.offsetHeight;
        if (h > 0 && h !== popupHeight) {
          setPopupHeight(h);
        }
      }
    },
    [parcelInfo],
  );

  const getPopupStyle = useCallback(
    () => ({
      position: 'fixed',
      left: `${coords.x + POPUP_OFFSET_X}px`,
      top: `${coords.y - popupHeight - POPUP_GAP_Y}px`,
      zIndex: 9999,
      pointerEvents: 'none',
      visibility: popupHeight > 0 ? 'visible' : 'hidden',
    }),
    [coords, popupHeight],
  );

  const getPopupInnerStyle = useCallback(
    () => ({
      position: 'relative',
      top: 'auto',
      left: 'auto',
      margin: 0,
    }),
    [],
  );

  return {
    year,
    setYear,
    minYear,
    maxYear,
    parcelInfo,
    modalCoords,
    parcelHoverCultureName,
    coords,
    map,
    polygonSource,
    mapElement,
    popup,
    popupContentRef,
    handleCultureHover,
    clearPopup,
    getPopupStyle,
    getPopupInnerStyle,
  };
};

export default MapInteractionHook;
